import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { useMontadoras } from '../../hooks/useMontadoras';
import { Montadora } from '../../types/montadora';
import { HeaderList } from '../../components/HeaderList';
import { TableList } from '../../components/TableList';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { Pagination } from '../../components/Pagination';

const MontadorasLista: React.FC = () => {
  const [busca, setBusca] = useState('');
  const [montadoraParaExcluir, setMontadoraParaExcluir] = useState<Montadora | null>(null);
  const { montadoras, listLoading, excluirMontadora, getLogoUrl } = useMontadoras();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleExcluir = async (montadora: Montadora) => {
    setMontadoraParaExcluir(montadora);
  };

  const confirmarExclusao = async () => {
    if (montadoraParaExcluir) {
      await excluirMontadora(montadoraParaExcluir.id);
      setMontadoraParaExcluir(null);
    }
  };

  const montadorasFiltradas = (montadoras || []).filter(montadora =>
    busca === '' || montadora.nome.toLowerCase().includes(busca.toLowerCase())
  );

  // Cálculo da paginação
  const totalPages = Math.ceil(montadorasFiltradas.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = montadorasFiltradas.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reseta para primeira página quando mudar o número de itens
  };

  // Resetar para primeira página quando houver uma nova busca
  useEffect(() => {
    setCurrentPage(1);
  }, [busca]);

  const columns = [
    { 
      header: 'Logo', 
      accessor: 'logo' as keyof Montadora,
      render: (montadora: Montadora) => {
        const logoUrl = montadora.logo ? getLogoUrl(montadora.logo) : null;
        return logoUrl ? (
          <img 
            src={logoUrl} 
            alt={`Logo ${montadora.nome}`}
            className="w-12 h-12 object-contain rounded-lg border border-gray-200"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
            <span className="text-gray-400 text-xs text-center">Sem logo</span>
          </div>
        );
      }
    },
    { header: 'Nome', accessor: 'nome' as keyof Montadora },
  ];

  const actions = [
    {
      icon: <Pencil size={20} />,
      onClick: (montadora: Montadora) => navigate(`/montadoras/${montadora.id}`),
      className: "text-blue-600 hover:text-blue-900"
    },
    {
      icon: <Trash2 size={20} />,
      onClick: handleExcluir,
      className: "text-red-600 hover:text-red-900"
    }
  ];

  if (listLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <HeaderList
        title="Montadoras"
        buttonLabel="Nova Montadora"
        searchPlaceholder="Buscar montadoras..."
        searchValue={busca}
        onSearchChange={setBusca}
        onButtonClick={() => navigate('/montadoras/novo')}
      />

      <TableList
        data={currentItems}
        columns={columns}
        actions={actions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={montadorasFiltradas.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
      />

      <ConfirmationModal
        isOpen={!!montadoraParaExcluir}
        onClose={() => setMontadoraParaExcluir(null)}
        onConfirm={confirmarExclusao}
        title="Excluir Montadora"
        message={`Tem certeza que deseja excluir a montadora ${montadoraParaExcluir?.nome}?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={listLoading}
      />
    </div>
  );
};

export default MontadorasLista; 